import { localeTagForAppLanguage } from '../i18n/languages';
import type { AppLanguage } from '../i18n/types';
import { profileStorageKey } from './profileStorageKey';

/** Last profile PATCH response timestamp cached on this device (per account). */
const SERVER_CACHE_PREFIX = 'ofrontend.profileServerUpdatedAt';
/** Fallback when PATCH succeeds but the response omits a timestamp. */
const LOCAL_SAVE_PREFIX = 'ofrontend.profileLastSavedAt';

/**
 * Profile-content update time from GET /profile.
 * Backend sets `profile_updated_at` / `profileUpdatedAt` on PATCH /profile only (not login).
 */
const PROFILE_UPDATED_AT_KEYS = [
  'profile_updated_at',
  'profileUpdatedAt',
  'profile_last_updated_at',
  'profileLastUpdatedAt',
] as const;

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function parseIsoMillis(iso: string | null | undefined): number | null {
  const trimmed = iso?.trim();
  if (!trimmed) {
    return null;
  }
  const ms = Date.parse(trimmed);
  return Number.isNaN(ms) ? null : ms;
}

function pickIsoString(raw: unknown): string | null {
  if (typeof raw !== 'string' || !raw.trim()) {
    return null;
  }
  const trimmed = raw.trim();
  return parseIsoMillis(trimmed) !== null ? trimmed : null;
}

function readProfileUpdatedAtFromRecord(record: Record<string, unknown> | null): string | null {
  if (!record) {
    return null;
  }
  for (const key of PROFILE_UPDATED_AT_KEYS) {
    const iso = pickIsoString(record[key]);
    if (iso) {
      return iso;
    }
  }
  for (const nestedKey of ['profile', 'user', 'data', 'private_client', 'privateClient'] as const) {
    const nested = record[nestedKey];
    if (isPlainObject(nested)) {
      const iso = readProfileUpdatedAtFromRecord(nested);
      if (iso) {
        return iso;
      }
    }
  }
  return null;
}

/** Dedicated profile update field from GET /profile (never generic `updated_at`). */
export function extractProfileUpdatedAtIso(data: unknown): string | null {
  if (isPlainObject(data)) {
    return readProfileUpdatedAtFromRecord(data);
  }
  return null;
}

/** After PATCH /profile, `updated_at` on the response reflects the save (unlike GET on login). */
function extractUpdatedAtFromPatchResponse(data: unknown): string | null {
  if (!isPlainObject(data)) {
    return null;
  }
  return pickIsoString(data.updated_at ?? data.updatedAt);
}

function getStoredIso(prefix: string, loginId?: string | null): string | null {
  try {
    const raw = localStorage.getItem(profileStorageKey(prefix, loginId));
    return pickIsoString(raw);
  } catch {
    return null;
  }
}

function persistStoredIso(prefix: string, iso: string, loginId?: string | null): void {
  try {
    localStorage.setItem(profileStorageKey(prefix, loginId), iso);
  } catch {
    /* ignore */
  }
}

export function getStoredProfileServerUpdatedAtIso(loginId?: string | null): string | null {
  return getStoredIso(SERVER_CACHE_PREFIX, loginId);
}

function getStoredProfileLastSavedAtIso(loginId?: string | null): string | null {
  return getStoredIso(LOCAL_SAVE_PREFIX, loginId);
}

export function markProfileSavedNow(loginId?: string | null, at: Date = new Date()): string {
  const iso = at.toISOString();
  persistStoredIso(LOCAL_SAVE_PREFIX, iso, loginId);
  return iso;
}

function persistProfileServerUpdatedAtIso(iso: string, loginId?: string | null): void {
  persistStoredIso(SERVER_CACHE_PREFIX, iso, loginId);
}

/**
 * Call after a successful PATCH /profile (or profile image upload).
 * Caches `profile_updated_at` or PATCH-response `updated_at` for display on this device.
 */
export function recordProfileUpdatedFromPatchResponse(
  data: unknown,
  loginId?: string | null,
): string | null {
  const fromDedicated = extractProfileUpdatedAtIso(data);
  const fromPatchResponse = extractUpdatedAtFromPatchResponse(data);
  const iso = fromDedicated ?? fromPatchResponse;
  if (iso) {
    persistProfileServerUpdatedAtIso(iso, loginId);
    return iso;
  }
  return markProfileSavedNow(loginId);
}

/** Newest known profile update: API field when set, else device PATCH cache / local fallback. */
export function resolveProfileLastUpdatedIso(
  apiProfileUpdatedAtIso: string | null | undefined,
  loginId?: string | null,
): string | null {
  const fromApi = pickIsoString(
    typeof apiProfileUpdatedAtIso === 'string' ? apiProfileUpdatedAtIso : null,
  );
  if (fromApi) {
    return fromApi;
  }

  const candidates = [
    getStoredProfileServerUpdatedAtIso(loginId),
    getStoredProfileLastSavedAtIso(loginId),
  ].filter((iso): iso is string => parseIsoMillis(iso) !== null);

  if (candidates.length === 0) {
    return null;
  }

  let best = candidates[0];
  let bestMs = parseIsoMillis(best)!;
  for (let i = 1; i < candidates.length; i += 1) {
    const ms = parseIsoMillis(candidates[i])!;
    if (ms > bestMs) {
      best = candidates[i];
      bestMs = ms;
    }
  }
  return best;
}

export function formatProfileLastUpdatedAt(
  iso: string | null | undefined,
  language: AppLanguage,
): string | null {
  const ms = parseIsoMillis(iso ?? null);
  if (ms === null) {
    return null;
  }
  const locale = localeTagForAppLanguage(language);
  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(ms));
  } catch {
    return iso?.trim() ?? null;
  }
}
